import { Card, CardContent } from './card';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { Badge } from './badge';

interface RiskGaugeCardProps {
    title: string;
    comment?: string;
    value: number | string;
    riskColor: string;
    riskLabel: string;
    hint?: string;
    comparisonText?: string;
    comparisonClassName?: string;
}

export function RiskGaugeCard({
    title,
    comment,
    value,
    riskColor,
    riskLabel,
    hint,
    comparisonText,
    comparisonClassName
}: RiskGaugeCardProps) {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    return (
        <Card className="rounded-lg shadow-sm border border-border relative">
            {hint && (
                <div className="absolute top-2 right-2 z-10">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-muted-foreground cursor-help hover:text-foreground transition-colors" />
                        </TooltipTrigger>
                        <TooltipContent
                            side="top"
                            align="center"
                            className="max-w-[300px] text-xs bg-popover text-foreground border border-border shadow-lg"
                            sideOffset={8}
                        >
                            {hint}
                        </TooltipContent>
                    </Tooltip>
                </div>
            )}
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <p className="text-sm font-medium text-muted-foreground">{title}</p>
                        <p className="text-4xl font-bold text-foreground mt-1 text-center" style={{ color: riskColor }}>
                            {value}
                        </p>

                        <div className="mb-3 flex justify-center">
                            <svg width="100" height="70" viewBox="0 0 100 70" style={{ overflow: 'visible' }}>
                                <defs>
                                    <linearGradient id={`gradient-${title}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#62a228" />
                                        <stop offset="33%" stopColor="#fe841c" />
                                        <stop offset="66%" stopColor="#e22223" />
                                        <stop offset="100%" stopColor="#8B0000" />
                                    </linearGradient>
                                </defs>

                                <path
                                    d="M 10 60 A 40 40 0 0 1 90 60"
                                    fill="none"
                                    stroke="#e5e7eb"
                                    strokeWidth="6"
                                    strokeLinecap="round"
                                />

                                <path
                                    d="M 10 60 A 40 40 0 0 1 90 60"
                                    fill="none"
                                    stroke={riskColor}
                                    strokeWidth="6"
                                    strokeLinecap="round"
                                    strokeDasharray={`${(numValue / 100) * 125.33} 251.33`}
                                />

                                <g transform={`translate(50, 60) rotate(${(numValue / 100) * 180 - 90})`}>
                                    <circle cx="0" cy="0" r="4" fill={riskColor} />
                                    <line x1="0" y1="0" x2="0" y2="-35" stroke={riskColor} strokeWidth="2" strokeLinecap="round" />
                                </g>

                                <text x="8" y="75" fontSize="10" fill="#9ca3af" textAnchor="start">
                                    0
                                </text>
                                <text x="95" y="75" fontSize="10" fill="#9ca3af" textAnchor="end">
                                    100
                                </text>
                            </svg>
                        </div>

                        <div className="flex items-center justify-center">
                            <Badge className="h-6 px-3 py-1 text-sm font-medium" style={{ backgroundColor: riskColor, color: 'white' }}>
                                {riskLabel}
                            </Badge>
                        </div>
                        {comment && (
                            <div className={`text-xs mt-3 pt-3 border-t border-muted text-center`}>
                                {comment}
                            </div>
                        )}
                        {/* {comparisonText && (
                            <div className={`text-xs mt-3 pt-3 border-t border-muted text-center ${comparisonClassName}`}>
                                {comparisonText}
                            </div>
                        )} */}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
